/**
 * Alert Rules Builder Component
 *
 * Per Plan: docs/USER_DEFINED_ALERTS_PLAN.md
 *
 * UI for creating and managing custom threshold-based alert rules:
 * - Rule list with enable/disable
 * - Rule creation wizard with friendly labels
 * - Rule testing against real data
 * - Metric, operator, and threshold selection
 */

import React, { useState, useEffect } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Collapse
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  PlayArrow,
  Save,
  Close,
  Refresh,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Rule,
  TrendingUp,
  TrendingDown
} from '@mui/icons-material';
import alertPreferencesService from '../../services/alertPreferencesService';
import { brandService } from '../../services/brandService';

// Severity colors
const SEVERITY_COLORS = {
  critical: 'error',
  high: 'warning',
  medium: 'info',
  low: 'success'
};

const AlertRulesBuilder = () => {
  const { confirmDialogProps, confirm } = useConfirmDialog();
  // State
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [brands, setBrands] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [operators, setOperators] = useState([]);
  const [comparisonPeriods, setComparisonPeriods] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);

  // Test result state
  const [testResults, setTestResults] = useState({});
  const [expandedRules, setExpandedRules] = useState({});

  // New rule form state
  const [ruleForm, setRuleForm] = useState({
    rule_name: '',
    rule_description: '',
    brand_id: 0,
    metric_name: '',
    operator: '',
    threshold_value: '',
    comparison_period: 'vs_yesterday',
    severity: 'medium',
    cooldown_hours: 24,
    is_enabled: true
  });

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rulesResult, brandsResult, metricsResult] = await Promise.all([
        alertPreferencesService.getRules(),
        brandService.getBrands(),
        alertPreferencesService.getAvailableMetrics()
      ]);

      setRules(rulesResult.rules || []);
      setBrands(brandsResult.brands || []);
      setMetrics(metricsResult.metrics || []);
      setOperators(metricsResult.operators || []);
      setComparisonPeriods(metricsResult.comparison_periods || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load rules and metrics.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Open dialog for creating new rule
  const handleOpenCreate = () => {
    setEditingRule(null);
    setRuleForm({
      rule_name: '',
      rule_description: '',
      brand_id: 0,
      metric_name: '',
      operator: '',
      threshold_value: '',
      comparison_period: 'vs_yesterday',
      severity: 'medium',
      cooldown_hours: 24,
      is_enabled: true
    });
    setDialogOpen(true);
  };

  // Open dialog for editing rule
  const handleOpenEdit = (rule) => {
    setEditingRule(rule);
    setRuleForm({
      rule_name: rule.rule_name || '',
      rule_description: rule.rule_description || '',
      brand_id: rule.brand_id || 0,
      metric_name: rule.metric_name || '',
      operator: rule.operator || '',
      threshold_value: rule.threshold_value || '',
      comparison_period: rule.comparison_period || 'vs_yesterday',
      severity: rule.severity || 'medium',
      cooldown_hours: rule.cooldown_hours || 24,
      is_enabled: rule.is_enabled !== undefined ? rule.is_enabled : true
    });
    setDialogOpen(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRule(null);
  };

  // Handle form field change
  const handleFormChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setRuleForm(prev => ({ ...prev, [field]: value }));
  };

  // Save rule
  const handleSaveRule = async () => {
    // Validate
    if (!ruleForm.rule_name || !ruleForm.metric_name || !ruleForm.operator || ruleForm.threshold_value === '') {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields.',
        severity: 'warning'
      });
      return;
    }

    setSaving(true);
    try {
      if (editingRule) {
        await alertPreferencesService.updateRule(editingRule.id, ruleForm);
        setSnackbar({
          open: true,
          message: 'Rule updated successfully!',
          severity: 'success'
        });
      } else {
        await alertPreferencesService.createRule(ruleForm);
        setSnackbar({
          open: true,
          message: 'Rule created successfully!',
          severity: 'success'
        });
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error saving rule:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save rule. Please try again.',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Delete rule
  const handleDeleteRule = async (ruleId) => {
    if (!await confirm({
      title: 'Delete Rule',
      message: 'Are you sure you want to delete this rule?',
      confirmText: 'Delete',
      severity: 'danger'
    })) {
      return;
    }

    try {
      await alertPreferencesService.deleteRule(ruleId);
      setSnackbar({
        open: true,
        message: 'Rule deleted successfully.',
        severity: 'success'
      });
      loadData();
    } catch (error) {
      console.error('Error deleting rule:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete rule.',
        severity: 'error'
      });
    }
  };

  // Toggle rule enabled status
  const handleToggleRule = async (rule) => {
    try {
      await alertPreferencesService.updateRule(rule.id, {
        is_enabled: !rule.is_enabled
      });
      setSnackbar({
        open: true,
        message: `Rule ${!rule.is_enabled ? 'enabled' : 'disabled'}.`,
        severity: 'success'
      });
      loadData();
    } catch (error) {
      console.error('Error toggling rule:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update rule status.',
        severity: 'error'
      });
    }
  };

  // Test rule
  const handleTestRule = async (ruleId) => {
    setTestResults(prev => ({ ...prev, [ruleId]: { loading: true } }));

    try {
      const result = await alertPreferencesService.testRule(ruleId);
      setTestResults(prev => ({
        ...prev,
        [ruleId]: {
          loading: false,
          ...result
        }
      }));
      setExpandedRules(prev => ({ ...prev, [ruleId]: true }));
    } catch (error) {
      console.error('Error testing rule:', error);
      setTestResults(prev => ({
        ...prev,
        [ruleId]: {
          loading: false,
          error: 'Failed to test rule'
        }
      }));
    }
  };

  // Toggle expanded state
  const toggleExpanded = (ruleId) => {
    setExpandedRules(prev => ({ ...prev, [ruleId]: !prev[ruleId] }));
  };

  // Get friendly operator label
  const getOperatorLabel = (operatorValue) => {
    const op = operators.find(o => o.name === operatorValue);
    return op?.label || operatorValue;
  };

  // Get friendly metric label
  const getMetricLabel = (metricValue) => {
    const metric = metrics.find(m => m.name === metricValue);
    return metric?.label || metricValue;
  };

  // Get brand name
  const getBrandName = (brandId) => {
    if (!brandId || brandId === 0) return 'All Brands';
    const brand = brands.find(b => b.id === brandId);
    return brand?.name || `Brand ${brandId}`;
  };

  // Get comparison period label
  const getComparisonPeriodLabel = (periodValue) => {
    const period = comparisonPeriods.find(p => p.name === periodValue);
    return period?.label || periodValue;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Rule sx={{ mr: 1, color: 'primary.main', fontSize: 32 }} />
          <Typography variant="h4" component="h1" color="primary">
            Custom Alert Rules
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadData}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
          >
            Create Rule
          </Button>
        </Box>
      </Box>

      {/* Explanation */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Create custom rules to get alerted when specific metrics cross your thresholds.
        For example, "Alert me when ROAS drops below 1.5" or "Alert me when spend increases by more than 20%".
      </Alert>

      {/* Rules List */}
      {rules.length === 0 ? (
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Rule sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Custom Rules Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first rule to get alerted when metrics cross your thresholds.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenCreate}
            >
              Create Your First Rule
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Rule</TableCell>
                <TableCell>Condition</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((rule) => (
                <React.Fragment key={rule.id}>
                  <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          {rule.rule_name}
                        </Typography>
                        {rule.rule_description && (
                          <Typography variant="body2" color="text.secondary">
                            {rule.rule_description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          label={getMetricLabel(rule.metric_name)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Typography variant="body2">
                          {getOperatorLabel(rule.operator)}
                        </Typography>
                        <Chip
                          label={rule.threshold_value}
                          size="small"
                        />
                        {rule.comparison_period && rule.operator?.includes('change') && (
                          <Typography variant="body2" color="text.secondary">
                            ({getComparisonPeriodLabel(rule.comparison_period)})
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getBrandName(rule.brand_id)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={rule.severity}
                        size="small"
                        color={SEVERITY_COLORS[rule.severity] || 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={rule.is_enabled === 1 || rule.is_enabled === true}
                        onChange={() => handleToggleRule(rule)}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Tooltip title="Test Rule">
                          <IconButton
                            size="small"
                            onClick={() => handleTestRule(rule.id)}
                            disabled={testResults[rule.id]?.loading}
                          >
                            {testResults[rule.id]?.loading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <PlayArrow />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Rule">
                          <IconButton size="small" onClick={() => handleOpenEdit(rule)}>
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Rule">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteRule(rule.id)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                        {testResults[rule.id] && !testResults[rule.id].loading && (
                          <IconButton size="small" onClick={() => toggleExpanded(rule.id)}>
                            {expandedRules[rule.id] ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Test Result Row */}
                  {testResults[rule.id] && !testResults[rule.id].loading && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0 }}>
                        <Collapse in={expandedRules[rule.id]} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 2, bgcolor: 'background.default', borderRadius: 1, my: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Test Result
                            </Typography>
                            {testResults[rule.id].error ? (
                              <Alert severity="error">{testResults[rule.id].error}</Alert>
                            ) : (
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  {testResults[rule.id].would_trigger ? (
                                    <Warning color="warning" />
                                  ) : (
                                    <CheckCircle color="success" />
                                  )}
                                  <Typography>
                                    {testResults[rule.id].would_trigger
                                      ? 'This rule WOULD trigger with current data'
                                      : 'This rule would NOT trigger with current data'}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {testResults[rule.id].message}
                                </Typography>
                                {testResults[rule.id].current_value !== null && (
                                  <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                                    <Typography variant="body2">
                                      Current Value: <strong>{testResults[rule.id].current_value}</strong>
                                    </Typography>
                                    {testResults[rule.id].baseline_value !== null && (
                                      <Typography variant="body2">
                                        Baseline: <strong>{testResults[rule.id].baseline_value}</strong>
                                      </Typography>
                                    )}
                                    {testResults[rule.id].change_pct !== null && (
                                      <Typography variant="body2">
                                        Change: <strong>{testResults[rule.id].change_pct?.toFixed(2)}%</strong>
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                                <Chip
                                  label={testResults[rule.id].test_status === 'evaluated' ? 'Real Data' : 'Simulated'}
                                  size="small"
                                  color={testResults[rule.id].test_status === 'evaluated' ? 'success' : 'default'}
                                  sx={{ mt: 1 }}
                                />
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Rule Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingRule ? 'Edit Rule' : 'Create Custom Rule'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Rule Name"
              value={ruleForm.rule_name}
              onChange={handleFormChange('rule_name')}
              placeholder="e.g., Low ROAS Alert"
              sx={{ mb: 2 }}
              required
            />

            <TextField
              fullWidth
              label="Description (optional)"
              value={ruleForm.rule_description}
              onChange={handleFormChange('rule_description')}
              placeholder="e.g., Alert when ROAS drops below target"
              sx={{ mb: 3 }}
              multiline
              rows={2}
            />

            {/* Rule Builder - Friendly format */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Alert me when{' '}
                <strong>{getMetricLabel(ruleForm.metric_name) || '[metric]'}</strong>{' '}
                is{' '}
                <strong>{getOperatorLabel(ruleForm.operator) || '[condition]'}</strong>{' '}
                <strong>{ruleForm.threshold_value || '[value]'}</strong>
              </Typography>
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Metric</InputLabel>
                  <Select
                    value={ruleForm.metric_name}
                    label="Metric"
                    onChange={handleFormChange('metric_name')}
                  >
                    {metrics.map(metric => (
                      <MenuItem key={metric.name} value={metric.name}>
                        <Box>
                          <Typography variant="body1">{metric.label}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {metric.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <FormControl fullWidth required>
                  <InputLabel>Condition</InputLabel>
                  <Select
                    value={ruleForm.operator}
                    label="Condition"
                    onChange={handleFormChange('operator')}
                  >
                    {operators.map(op => (
                      <MenuItem key={op.name} value={op.name}>
                        <Box>
                          <Typography variant="body1">{op.label}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {op.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={ruleForm.operator?.includes('pct') ? 'Percentage (%)' : 'Value'}
                  type="number"
                  value={ruleForm.threshold_value}
                  onChange={handleFormChange('threshold_value')}
                  required
                  InputProps={{
                    endAdornment: ruleForm.operator?.includes('pct') ? '%' : null
                  }}
                />
              </Grid>

              {ruleForm.operator?.includes('change') && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Compare Against</InputLabel>
                    <Select
                      value={ruleForm.comparison_period}
                      label="Compare Against"
                      onChange={handleFormChange('comparison_period')}
                    >
                      {comparisonPeriods.map(period => (
                        <MenuItem key={period.name} value={period.name}>
                          {period.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Brand</InputLabel>
                  <Select
                    value={ruleForm.brand_id}
                    label="Brand"
                    onChange={handleFormChange('brand_id')}
                  >
                    <MenuItem value={0}>All Brands</MenuItem>
                    {brands.map(brand => (
                      <MenuItem key={brand.id} value={brand.id}>
                        {brand.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Severity</InputLabel>
                  <Select
                    value={ruleForm.severity}
                    label="Severity"
                    onChange={handleFormChange('severity')}
                  >
                    <MenuItem value="critical">Critical</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Cooldown (hours)"
                  type="number"
                  value={ruleForm.cooldown_hours}
                  onChange={handleFormChange('cooldown_hours')}
                  helperText="Minimum hours between triggers"
                  InputProps={{ inputProps: { min: 1, max: 168 } }}
                />
              </Grid>

              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={ruleForm.is_enabled}
                      onChange={handleFormChange('is_enabled')}
                    />
                  }
                  label="Enabled"
                  sx={{ mt: 1 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} startIcon={<Close />}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveRule}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
          >
            {editingRule ? 'Update Rule' : 'Create Rule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ConfirmDialog {...confirmDialogProps} />
    </Container>
  );
};

export default AlertRulesBuilder;
